import { useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FilterButton } from './FilterButton'
import { useFiltersSlot } from './ListCard'
import { numericValue, type StatMetric, type StatsRow } from './statsColumns'
import { useStatLabel, type StatLabel } from '../hooks/useStatLabel'

/** Keeps the rows whose `metric` is above or below `value`. */
export interface StatThreshold {
  metric: string
  op: 'gt' | 'lt'
  /** As the user reads the metric: a rate in percent, money in its amount. */
  value: number | null
}

const OPS: StatThreshold['op'][] = ['gt', 'lt']

/**
 * Does a row meet every threshold? A threshold still missing its value keeps
 * everything, so a condition being typed in does not empty the table.
 */
export function meetsThresholds(
  stats: StatsRow,
  thresholds: StatThreshold[],
  metrics: StatMetric[]
): boolean {
  return thresholds.every(({ metric, op, value }) => {
    if (value === null) return true
    const raw = numericValue(stats[metric]) ?? 0
    // The API reports its rates as 0-1 fractions; people type them in percent.
    const format = metrics.find((m) => m.key === metric)?.format
    const actual = format === 'ratio' ? raw * 100 : raw
    return op === 'gt' ? actual > value : actual < value
  })
}

interface StatThresholdFilterProps {
  /** Metrics a condition can be put on. */
  metrics: StatMetric[]
  value: StatThreshold[]
  onChange: (thresholds: StatThreshold[]) => void
  /** Names a metric — from `useStatLabel`, so it reads as this network words it. */
  label: StatLabel
}

/**
 * Filters stats rows client-side on conditions like "CTR below 0.1 %" or
 * "impressions above 1 000", all of which a row has to meet.
 */
export function StatThresholdFilter({ metrics, value, onChange, label }: StatThresholdFilterProps) {
  const { t } = useTranslation()
  const metricLabel = (key: string) => {
    const metric = metrics.find((m) => m.key === key)
    return metric ? label(metric.labelKey) : key
  }
  const update = (index: number, patch: Partial<StatThreshold>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <FilterButton
      activeCount={value.filter((row) => row.value !== null).length}
      onReset={() => onChange([])}
    >
      {value.map((row, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <Select value={row.metric} onValueChange={(metric) => update(index, { metric })}>
              <SelectTrigger className="h-8 min-w-0 flex-1 text-sm">
                <SelectValue>{metricLabel(row.metric)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {metrics.map((metric) => (
                  <SelectItem key={metric.key} value={metric.key}>
                    {label(metric.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-(--ad-text-muted)"
              aria-label={t('common.remove')}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <X className="size-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Select
              value={row.op}
              onValueChange={(op) => update(index, { op: op as StatThreshold['op'] })}
            >
              <SelectTrigger className="h-8 w-28 text-sm">
                <SelectValue>{t(`stats.threshold.${row.op}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {OPS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`stats.threshold.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NumberInput
              className="h-8 min-w-0 flex-1 text-sm"
              value={row.value}
              onChange={(next) => update(index, { value: next })}
              aria-label={metricLabel(row.metric)}
            />
            {/* Rates are typed in percent, so the unit stays in sight once a value is in. */}
            {metrics.find((m) => m.key === row.metric)?.format === 'ratio' && (
              <span className="text-sm text-(--ad-text-muted)">%</span>
            )}
          </div>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        onClick={() =>
          onChange([...value, { metric: metrics[0]?.key ?? 'impressions', op: 'gt', value: null }])
        }
      >
        <Plus className="size-4" />
        {t('stats.threshold.add')}
      </Button>
    </FilterButton>
  )
}

/**
 * Threshold filtering for a list of stats rows: the conditions, and the rows
 * that meet them. `statsOf` reads a row's metrics.
 */
export function useStatThresholds<T>(
  rows: T[],
  statsOf: (row: T) => StatsRow,
  metrics: StatMetric[]
) {
  const [thresholds, setThresholds] = useState<StatThreshold[]>([])
  const filtered = useMemo(
    () => rows.filter((row) => meetsThresholds(statsOf(row), thresholds, metrics)),
    // statsOf is a plain accessor, read fresh each time the rows or conditions change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, thresholds, metrics]
  )
  return { thresholds, setThresholds, filtered }
}

interface StatFilterBarProps {
  metrics: StatMetric[]
  value: StatThreshold[]
  onChange: (thresholds: StatThreshold[]) => void
  /** Rows left after filtering, and rows there were. */
  shown: number
  total: number
  /** A note for above the table, such as why its rows cannot be acted on. */
  children?: ReactNode
}

/**
 * A stats table's threshold filter and how many rows it left. Inside a
 * `ListCard` it sits in the card's header, after the column picker; elsewhere
 * it is a toolbar over the table.
 */
export function StatFilterBar({ metrics, value, onChange, shown, total, children }: StatFilterBarProps) {
  const { t } = useTranslation()
  const statLabel = useStatLabel()
  const slot = useFiltersSlot()

  const filter = (
    <>
      <StatThresholdFilter metrics={metrics} value={value} onChange={onChange} label={statLabel} />
      {/* Shown whenever a condition is set, so one every row meets still reads as applied. */}
      {value.some((threshold) => threshold.value !== null) && (
        <span className="text-xs text-(--ad-text-muted)">
          {t('stats.threshold.showing', { count: shown, total })}
        </span>
      )}
    </>
  )
  const note = children && (
    <div className="px-(--card-spacing) pb-2 text-xs text-(--ad-text-muted)">{children}</div>
  )

  if (slot) {
    return (
      <>
        {slot.element && createPortal(filter, slot.element)}
        {note}
      </>
    )
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-(--card-spacing) pb-2">{filter}</div>
      {note}
    </>
  )
}
